import dayjs from "dayjs";

import { filterByDateRange } from "@aclymatepackages/date-helpers";

const isTrackedEmployee = ({ status }) =>
  status !== undefined && status !== "terminated";

const formatBlockerMonth = (date) => dayjs(date).format("YYYY-MM");

const groupUtilityGapsByOfficeAndSubcategory = (defaultUtilityEmissions) => {
  const groups = defaultUtilityEmissions.reduce((acc, emission) => {
    const { office = {}, subcategory, date } = emission;
    const officeId = office?.id || "no-office";
    const officeName = office?.name || "Unassigned office";
    const key = `${officeId}::${subcategory}`;
    const existing = acc[key] || {
      type: "utility-gap",
      officeId,
      officeName,
      subcategory,
      months: []
    };
    return {
      ...acc,
      [key]: { ...existing, months: [...existing.months, date] }
    };
  }, {});

  return Object.values(groups).map((group) => ({
    ...group,
    months: group.months.map(formatBlockerMonth).sort()
  }));
};

const buildEmployeeBlockers = ({
  trackedEmployees,
  employeeCount,
  accountingDate
}) => {
  const inScopeEmployees = trackedEmployees.filter(({ startDate }) =>
    dayjs(startDate).isBefore(dayjs(accountingDate))
  );

  const statusBlockers = inScopeEmployees
    .filter(
      ({ status }) => status === "unconfirmed" || status === "incomplete"
    )
    .map(({ id, firstName, lastName, status }) => ({
      type: "employee-status",
      employeeId: id,
      firstName,
      lastName,
      status
    }));

  if (!employeeCount) {
    return statusBlockers;
  }

  const remainingEmployees = employeeCount - trackedEmployees.length;
  const countBlockers = remainingEmployees
    ? [
        {
          type: "employee-count-gap",
          expected: employeeCount,
          actual: trackedEmployees.length
        }
      ]
    : [];

  return [...statusBlockers, ...countBlockers];
};

export const findAccountingBlockers = ({
  allEmissions = [],
  startDate,
  employeeCount,
  mostRecentAccountingDate,
  employees = [],
  accountingDate
}) => {
  if (!accountingDate) {
    return { isBlocked: false, blockers: [] };
  }

  const emissionsInWindow = allEmissions.filter(({ date }) =>
    filterByDateRange({
      filterStartDate: mostRecentAccountingDate || startDate,
      filterEndDate: accountingDate,
      date
    })
  );

  const defaultUtilityEmissions = emissionsInWindow.filter(
    ({ source }) => source === "default-utilities"
  );

  const utilityBlockers = groupUtilityGapsByOfficeAndSubcategory(
    defaultUtilityEmissions
  );

  const trackedEmployees = employees.filter(isTrackedEmployee);
  const employeeBlockers = buildEmployeeBlockers({
    trackedEmployees,
    employeeCount,
    accountingDate
  });

  const blockers = [...employeeBlockers, ...utilityBlockers];

  return {
    isBlocked: blockers.length > 0,
    blockers
  };
};

const employeeCountGapMessage = ({ expected, actual }) => {
  const diff = expected - actual;
  if (diff > 0) {
    const noun = diff === 1 ? "person" : "people";
    return `Headcount is set to ${expected} but only ${actual} ${actual === 1 ? "person is" : "people are"} tracked — add ${diff} more ${noun}, or update the headcount.`;
  }
  const extras = Math.abs(diff);
  return `Headcount is set to ${expected} but ${actual} people are tracked — remove ${extras} or update the headcount.`;
};

const employeeStatusMessage = ({ firstName, lastName, status }) => {
  const name = [firstName, lastName].filter(Boolean).join(" ") || "An employee";
  const statusText =
    status === "incomplete"
      ? "has an incomplete commute survey"
      : "has not confirmed their commute survey";
  return `${name} ${statusText}.`;
};

const utilityGapMessage = ({ officeName, subcategory, months }) => {
  const noun = months.length === 1 ? "month" : "months";
  return `${officeName} is missing ${months.length} ${noun} of ${subcategory} entries (${months.join(", ")}).`;
};

const blockerMessageFormatters = {
  "employee-count-gap": employeeCountGapMessage,
  "employee-status": employeeStatusMessage,
  "utility-gap": utilityGapMessage
};

export const formatBlockerMessage = (blocker) => {
  const formatter = blockerMessageFormatters[blocker.type];
  return formatter ? formatter(blocker) : "Unknown blocker";
};
